import { DomainEvent, EventPayload, AnalyticsEvent } from './types'
import { Transport } from './transport/Transport'
import { ConsentStore } from './consent/ConsentStore'
import { newEventId } from './util/eventId'
import { readFbCookies, readGaClientId } from './util/fbCookies'
import { getAnalyticsIdentity } from './identity'
import { logger } from './util/logger'
import { incCounter } from './util/debugCounters'

// In-memory dedup cache — defence against StrictMode double-render or accidental
// double-dispatch of the same event_id. Max 500 entries; oldest evicted when full.
const DEDUP_MAX = 500
const recentEventIds = new Set<string>()

function markSeen(id: string): boolean {
  if (recentEventIds.has(id)) return false
  if (recentEventIds.size >= DEDUP_MAX) {
    // Set preserves insertion order; delete the first (oldest) entry.
    recentEventIds.delete(recentEventIds.values().next().value!)
  }
  recentEventIds.add(id)
  return true
}

export class AnalyticsClient {
  private readonly transports: Transport[]
  private readonly consent: ConsentStore

  constructor(transports: Transport[], consent: ConsentStore) {
    this.transports = transports
    this.consent = consent
  }

  track(name: DomainEvent, payload: EventPayload = {}): void {
    const { fbp, fbc } = readFbCookies()
    const identity = getAnalyticsIdentity()
    const event: AnalyticsEvent = {
      name,
      event_id: payload.event_id ?? newEventId(),
      event_time: Math.floor(Date.now() / 1000),
      event_source_url: typeof window === 'undefined' ? '' : window.location.href,
      payload,
      consent: this.consent.snapshot(),
      fbp,
      fbc,
      ga_client_id: readGaClientId(),
      external_id: identity.external_id,
      email: identity.email,
      phone: identity.phone,
    }

    if (!markSeen(event.event_id)) {
      incCounter('dropped')
      logger.debug('Duplicate event_id, skipping:', event.event_id)
      return
    }

    if (this.transports.length === 0) {
      incCounter('dropped')
      return
    }

    for (const t of this.transports) {
      try {
        t.send(event)
      } catch (e) {
        logger.debug(`[${t.name}]`, e)
      }
    }
  }

  flush(): void {
    this.transports.forEach(t => t.flush?.())
  }

  dispose(): void {
    this.flush()
  }
}
